import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Cookies from "js-cookie";
import "./ContactInfo.css";
import "../../components/header/userMenu/SettingsPrivacy.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default function AccountOwnershipPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state);

  const [showDeactivateAccountModal, setShowDeactivateAccountModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeactivatingAccount, setIsDeactivatingAccount] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  const clearSessionAndLogout = () => {
    Cookies.remove("user");
    localStorage.removeItem("user");
    sessionStorage.clear();
    dispatch({ type: "LOGOUT" });
  };

  const handleDeactivateAccount = async () => {
    setIsDeactivatingAccount(true);
    try {
      if (!user || !user.token) {
        throw new Error("Authentication token is missing. Please log in again.");
      }

      const res = await axios.put(
        `${API_URL}/deactivateAccount`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data && res.data.success) {
        setShowDeactivateAccountModal(false);
        clearSessionAndLogout();
        navigate("/login", { replace: true });
        return;
      }

      throw new Error(res.data?.message || "Failed to deactivate account.");
    } catch (err) {
      console.error("Failed to deactivate account:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to deactivate account. Please try again.";
      alert(errorMessage);

      if (
        err.response?.status === 400 &&
        err.response?.data?.message?.includes("Authentification")
      ) {
        clearSessionAndLogout();
        navigate("/login", { replace: true });
      }
    } finally {
      setIsDeactivatingAccount(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      if (!user || !user.token) {
        throw new Error("Authentication token is missing. Please log in again.");
      }

      const res = await axios.delete(`${API_URL}/deleteAccount`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data && res.data.success) {
        setShowDeleteAccountModal(false);
        clearSessionAndLogout();
        navigate("/login", { replace: true });
        return;
      }

      throw new Error(res.data?.message || "Failed to delete account.");
    } catch (err) {
      console.error("Failed to delete account:", err);
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to delete account. Please try again.";
      alert(errorMessage);

      if (err.response?.status === 400 && err.response?.data?.message?.includes("Authentification")) {
        clearSessionAndLogout();
        navigate("/login", { replace: true });
      }
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <div className="contact_info_page">
      <header className="contact_info_page_header">
        <button className="contact_info_page_back" onClick={handleBack} aria-label="Go back">
          <BackIcon />
        </button>
        <h1>Account ownership and control</h1>
      </header>

      <main className="contact_info_page_body">
        <div className="contact_info_page_card">
          <p className="contact_info_page_description">
            Manage your data, modify your legacy contact, and deactivate or delete your accounts and
            profiles.
          </p>

          <div className="account_ownership_actions">
            <div
              className="account_ownership_action"
              onClick={() => setShowDeactivateAccountModal(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowDeactivateAccountModal(true);
                }
              }}
            >
              <div className="account_ownership_action_content">
                <h3 className="account_ownership_action_title">Deactivate Account</h3>
                <p className="account_ownership_action_description">
                  Temporarily deactivate your account for up to 15 days. You can reactivate it
                  anytime by logging in. If not reactivated within 15 days, your account and all
                  associated data will be permanently deleted.
                </p>
              </div>
              <div className="account_ownership_action_arrow">
                <ChevronRightIcon />
              </div>
            </div>

            <div
              className="account_ownership_action account_ownership_action_destructive"
              onClick={() => setShowDeleteAccountModal(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowDeleteAccountModal(true);
                }
              }}
            >
              <div className="account_ownership_action_content">
                <h3 className="account_ownership_action_title">Delete Account</h3>
                <p className="account_ownership_action_description">
                  Permanently delete your account and all of your data. This action cannot be undone.
                </p>
              </div>
              <div className="account_ownership_action_arrow">
                <ChevronRightIcon />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Deactivate Account Confirmation Modal */}
      {showDeactivateAccountModal && (
        <div
          className="delete_account_modal_overlay"
          onClick={() => {
            if (!isDeactivatingAccount) {
              setShowDeactivateAccountModal(false);
            }
          }}
        >
          <div className="delete_account_modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete_account_modal_header">
              <h2 className="delete_account_modal_title">Deactivate account?</h2>
              <button
                className="delete_account_modal_close"
                onClick={() => {
                  if (!isDeactivatingAccount) {
                    setShowDeactivateAccountModal(false);
                  }
                }}
                aria-label="Close"
                disabled={isDeactivatingAccount}
              >
                <XIcon />
              </button>
            </div>

            <div className="delete_account_modal_content">
              <p className="delete_account_warning_text">
                Your account will be temporarily deactivated for up to 15 days. You can reactivate
                it anytime by logging in. If you don't reactivate within 15 days, your account and
                all associated data will be permanently deleted.
              </p>

              <div className="delete_account_modal_actions">
                <button
                  className="delete_account_cancel_btn"
                  onClick={() => {
                    if (!isDeactivatingAccount) {
                      setShowDeactivateAccountModal(false);
                    }
                  }}
                  disabled={isDeactivatingAccount}
                >
                  Cancel
                </button>
                <button
                  className="delete_account_confirm_btn"
                  onClick={handleDeactivateAccount}
                  disabled={isDeactivatingAccount}
                  style={{
                    opacity: isDeactivatingAccount ? 0.6 : 1,
                    cursor: isDeactivatingAccount ? "not-allowed" : "pointer",
                  }}
                >
                  {isDeactivatingAccount ? "Deactivating..." : "Deactivate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div
          className="delete_account_modal_overlay"
          onClick={() => {
            if (!isDeletingAccount) {
              setShowDeleteAccountModal(false);
            }
          }}
        >
          <div className="delete_account_modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete_account_modal_header">
              <h2 className="delete_account_modal_title">Delete account?</h2>
              <button
                className="delete_account_modal_close"
                onClick={() => {
                  if (!isDeletingAccount) {
                    setShowDeleteAccountModal(false);
                  }
                }}
                aria-label="Close"
                disabled={isDeletingAccount}
              >
                <XIcon />
              </button>
            </div>

            <div className="delete_account_modal_content">
              <p className="delete_account_warning_text">
                You'll no longer be able to use this account to log in, reset your password or receive
                notifications.
              </p>

              <div className="delete_account_modal_actions">
                <button
                  className="delete_account_cancel_btn"
                  onClick={() => {
                    if (!isDeletingAccount) {
                      setShowDeleteAccountModal(false);
                    }
                  }}
                  disabled={isDeletingAccount}
                >
                  Cancel
                </button>
                <button
                  className="delete_account_confirm_btn"
                  onClick={handleDeleteAccount}
                  disabled={isDeletingAccount}
                  style={{
                    opacity: isDeletingAccount ? 0.6 : 1,
                    cursor: isDeletingAccount ? "not-allowed" : "pointer",
                  }}
                >
                  {isDeletingAccount ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
